import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Store, FileText, Facebook, Instagram, Youtube, Linkedin, MoreHorizontal, ChevronDown, Users, Printer, Landmark } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSocialLinks } from "@/hooks/useSocialLinks";
import { PWAInstallButton } from "@/components/PWAInstallButton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useState } from "react";

export function MoreMenuPopover() {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { data: socialSettings } = useSocialLinks();
  const { user } = useAuth();

  // Check if user has commercial role
  const { data: hasCommercialRole } = useQuery({
    queryKey: ["commercial-role-check-menu", user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .in("role", ["super_admin", "admin"]);
      if (error) return false;
      return data && data.length > 0;
    },
    enabled: !!user?.id,
  });

  const menuLinks = [
    { to: "/impressoras-uv", icon: Printer, label: "Impressoras UV" },
    { to: "/financiamento", icon: Landmark, label: "Financiamento" },
    { to: "/loja", icon: Store, label: t("nav.shop") },
    { to: "/blog", icon: FileText, label: t("nav.blog") },
  ];

  const socialLinks = [
    { key: "facebook_url", name: "Facebook", icon: Facebook, color: "#1877F2" },
    { key: "instagram_url", name: "Instagram", icon: Instagram, color: "#E4405F" },
    { key: "youtube_url", name: "YouTube", icon: Youtube, color: "#FF0000" },
    { key: "linkedin_url", name: "LinkedIn", icon: Linkedin, color: "#0A66C2" },
  ].filter(link => (socialSettings?.[link.key] ?? "").trim() !== "");

  const handleLinkClick = () => {
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "inline-flex h-10 items-center justify-center rounded-md px-2 xl:px-3 py-2 text-sm font-medium",
            "bg-transparent text-foreground transition-colors",
            "hover:text-primary hover:bg-accent",
            "focus:outline-none focus:ring-2 focus:ring-primary/20",
            open && "bg-accent text-primary"
          )}
        >
          <MoreHorizontal className="h-4 w-4 mr-1" />
          <span className="text-sm">{t("moreMenu.more")}</span>
          <ChevronDown className={cn(
            "ml-1 h-3 w-3 transition-transform duration-200",
            open && "rotate-180"
          )} />
        </button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[220px] p-0 border-border"
        align="start"
        sideOffset={8}
      >
        <div className="p-3 bg-popover rounded-lg">
          {/* Navigation Links */}
          <div className="space-y-1">
            {menuLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={handleLinkClick}
                  className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md hover:bg-accent hover:text-primary transition-colors group"
                >
                  <Icon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* CRM Access for Commercial Users */}
          {hasCommercialRole && (
            <div className="border-t border-border mt-2 pt-2">
              <Link
                to="/crm"
                onClick={handleLinkClick}
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md bg-violet-500/10 text-violet-600 hover:bg-violet-500/20 transition-colors group"
              >
                <Users className="h-4 w-4" />
                CRM Comercial
              </Link>
            </div>
          )}

          {/* PWA Install */}
          <div className="border-t border-border mt-2 pt-2">
            <div className="px-1">
              <PWAInstallButton 
                variant="ghost" 
                size="sm" 
                className="w-full justify-start gap-3 px-2 text-sm font-medium hover:bg-accent hover:text-primary"
                showIcon={true}
              />
            </div>
          </div>

          {/* Social Links */}
          {socialLinks.length > 0 && (
            <div className="border-t border-border mt-2 pt-3">
              <p className="px-3 text-xs font-medium text-muted-foreground mb-2">
                {t("moreMenu.followUs")}
              </p>
              <div className="flex items-center gap-1 px-2">
                {socialLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <a
                      key={link.key}
                      href={socialSettings?.[link.key]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                      aria-label={link.name}
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
